import React, { Fragment, useEffect, useState } from "react";
import { Button, Modal, Card, Container } from "react-bootstrap";
import { FaShoppingCart, FaShoppingBag, FaArrowLeft } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import OrderDetails from "./OrderDetails";
import OrderRelatedDetails from "./OrderRelatedDetails";
import JajotApi from "../../api/JajotApi";
import toast from "react-hot-toast";

const OrderWizard = ({ show, onHide }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // State variables
  const [ledgers, setLedgers] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [operation, setOperation] = useState("model");
  const [active, setActive] = useState(location.state?.type || "");

  const [orderDetails, setOrderDetails] = useState({
    date: new Date().toISOString().split("T")[0],
    number: "Generating Order Id",
    type: location.state?.type || "Purchase",
    status: "Completed",
    partyLedger: "",
    salesLedger: "",
  });

  const [lineItems, setLineItems] = useState([
    { item: "", rate: 0, quantity: 1, units: "", amount: 0 },
  ]);
  const [showErrors, setShowErrors] = useState({});

  useEffect(() => {
    if (location.pathname.includes("create")) setOperation("create");
    else if (location.pathname.includes("edit")) setOperation("edit");
    else setOperation("model");
  }, [location.pathname]);

  useEffect(() => {
    if (operation !== "model") {
      getLedgers();
      getStockItems();
    }
    if (operation === "create") {
      console.log(orderDetails.type);

      getNextOrderId();
    }
  }, [operation]);

  async function getLedgers() {
    try {
      const res = await JajotApi.getLedgerRecords();
      if (res?.success) {
        const sortedLedgers = res.records
          .map((r) => ({
            label: r.Name,
            groupLabel: r.Parent_Group__c,
            value: r.Id,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)); // Sort A-Z

        setLedgers(sortedLedgers);
      }
    } catch (err) {
      console.error("Ledger error:", err);
    }
  }

  async function getStockItems() {
    try {
      const res = await JajotApi.getStockItemRecords();
      if (res?.success) {
        const sortedStockItems = res.records
          .map((r) => ({
            label: r.Name,
            groupLabel: r.category || "Stock Items",
            value: r.Id,
            rate: r.Rate__c,
            units: r.Units__c,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)); // Sort A-Z

        setStockItems(sortedStockItems);
      }
    } catch (err) {
      console.error("Stock error:", err);
    }
  }

  async function getNextOrderId() {
    try {
      const res = await JajotApi.getLastOrder();
      const last = res?.records?.[0]?.Name || "ON-0000";
      const num = parseInt(last.split("-")[1] || "0") + 1;
      setOrderDetails((prev) => ({
        ...prev,
        number: `ON-${num.toString().padStart(4, "0")}`,
      }));
    } catch (err) {
      console.error("Order ID error:", err);
    }
  }

  async function createOrder() {
    if (!orderDetails.partyLedger?.value) {
      toast.error("Party Ledger is required");
      setShowErrors((prev) => ({ ...prev, partyLedger: true }));
      return;
    }

    if (!orderDetails.salesLedger?.value) {
      toast.error("Sales Ledger is required");
      setShowErrors((prev) => ({ ...prev, salesLedger: true }));
      return;
    }

    const invalidItemIndex = lineItems.findIndex((i) => !i.item);
    if (invalidItemIndex !== -1) {
      toast.error("Please fill all required Item fields");
      setShowErrors((prev) => ({
        ...prev,
        item: true,
        itemIdx: invalidItemIndex,
      }));
      return;
    }

    try {
      const payload = {
        orderDetail: {
          Order_Date__c: orderDetails.date,
          Order_Status__c: orderDetails.status,
          Party_Ledger__c: orderDetails.partyLedger?.value || null,
          Sales_Ledger__c: orderDetails.salesLedger?.value || null,
          Type__c: orderDetails.type,
          LineItems: lineItems.map((item) => ({
            Amount__c: item.amount,
            Quantity__c: item.quantity,
            Rate__c: item.rate,
            Ledger__c: item.isLedger ? item.item?.value : null,
            Stock_Item__c: !item.isLedger ? item.item?.value : null,
          })),
        },
      };

      const res = await JajotApi.createRecord("Order__c", payload);
      if (!res.success) {
        toast.error("Failed to create order");
        return;
      }
      toast.success("Order created successfully");
      navigate("/order");
    } catch (err) {
      console.error("Create order error:", err);
      toast.error("Error creating order");
    }
  }

  async function updateOrder() {
    if (!orderDetails.partyLedger?.value) {
      toast.error("Party Ledger is required");
      return;
    }
    if (!orderDetails.salesLedger?.value) {
      toast.error("Sales Ledger is required");
      return;
    }
    const recordId = location.state?.existingOrder?.details?.id;
    if (!recordId) {
      toast.error("No record id found!");
      return;
    }

    try {
      const payload = {
        orderDetail: {
          Id: recordId,
          Order_Date__c: orderDetails.date,
          Type__c: orderDetails.type,
          Order_Status__c: orderDetails.status,
          Sales_Ledger__c: orderDetails.salesLedger?.value,
          LineItems: lineItems.map((i) => ({
            Id: i.id || undefined,
            Amount__c: i.amount,
            Quantity__c: i.quantity,
            Rate__c: i.rate,
            Ledger__c: i.isLedger ? i.item?.value : null,
            Stock_Item__c: !i.isLedger ? i.item?.value : null,
          })),
        },
      };

      const res = await JajotApi.updateRecord("Order__c", recordId, payload);

      if (res.success) {
        toast.success("Order updated successfully");
        navigate("/order");
      } else {
        toast.error("Failed to update order");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating order");
    }
  }

  useEffect(() => {
    if (operation === "edit") {
      setOrderDetails(location.state?.existingOrder?.details);
      setLineItems(location.state?.existingOrder?.items);
      setActive(location.state?.existingOrder?.details?.type);
    }
  }, [operation, location.state]);

  function nextPage() {
    navigate("/order/create", { state: { type: active } });
  }

  return (
    <Fragment>
      {operation === "model" && (
        <Modal show={show} onHide={onHide} size="lg" centered>
          <Modal.Header closeButton className="bg-light">
            <div>
              <h3 className="text-primary mb-1">Create New Order</h3>
              <small className="text-muted">
                Select the type of order you want to create
              </small>
            </div>
          </Modal.Header>

          <Modal.Body className="d-flex justify-content-center flex-wrap gap-4 py-4">
            <Card
              onClick={() => setActive("Sales")}
              className={`p-4 text-center shadow-sm border rounded ${active === "Sales"
                ? "border-primary bg-light"
                : "border-light bg-white"
                }`}
              style={{
                width: "14rem",
                transition: "all 0.3s",
                transform: active === "Sales" ? "scale(1.05)" : "scale(1)",
                cursor: "pointer",
              }}
            >
              <div className="mb-3">
                <FaShoppingCart size={40} className="text-success" />
              </div>
              <h5 className="fw-bold">Sales Order</h5>
              <p className="text-muted small">
                Create and manage sales orders for your customers.
              </p>
            </Card>

            <Card
              onClick={() => setActive("Purchase")}
              className={`p-4 text-center shadow-sm border rounded ${active === "Purchase"
                ? "border-primary bg-light"
                : "border-light bg-white"
                }`}
              style={{
                width: "14rem",
                transition: "all 0.3s",
                transform: active === "Purchase" ? "scale(1.05)" : "scale(1)",
                cursor: "pointer",
              }}
            >
              <div className="mb-3">
                <FaShoppingBag size={40} className="text-primary" />
              </div>
              <h5 className="fw-bold">Purchase Order</h5>
              <p className="text-muted small">
                Create and manage purchase orders for your suppliers.
              </p>
            </Card>
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-between">
            <Button variant="outline-secondary" onClick={onHide}>
              &larr; Back To Orders
            </Button>
            <Button
              variant="primary"
              className="fw-bold px-4"
              onClick={nextPage}
              disabled={active.length === 0}
            >
              Continue &rarr;
            </Button>
          </Modal.Footer>
        </Modal>
      )}
      {(operation === "create" || operation === "edit") && (
        <Container fluid className="py-4">
          <div className="bg-white shadow-sm rounded p-3">
            <div className="mb-4 d-flex align-items-center gap-3">
              <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                <FaArrowLeft className="me-2" /> Back To Orders
              </Button>
              <h3 className="text-primary mb-0">
                {operation === "edit"
                  ? "Edit Order"
                  : active === "Sales"
                    ? "Create Sales Order"
                    : "Create Purchase Order"}
              </h3>
            </div>

            <OrderDetails
              ledgersList={ledgers}
              details={orderDetails}
              setDetails={setOrderDetails}
              type={active}
              lineItems={lineItems}
              mode={operation}
              showErrors={showErrors}
            />

            <OrderRelatedDetails
              stockItemList={stockItems}
              ledgersList={ledgers}
              lineItems={lineItems}
              setLineItems={setLineItems}
              mode={operation}
              showErrors={showErrors}
            />

            <div className="text-center mt-4">
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  console.log("Order Details:", orderDetails);
                  console.log("Line Items:", lineItems);
                  if (operation === "edit") {
                    updateOrder();
                  } else {
                    console.log(orderDetails);
                    createOrder();
                  }
                }}
              >
                {operation === "edit" ? "Update Order" : "Create Order"}
              </Button>
            </div>
          </div>
        </Container>
      )}
    </Fragment>
  );
};

export default OrderWizard;
